<template>
  <div class="callback-page">
    <div class="callback-card" :class="{ 'animate-in': pageLoaded }">
      <!-- 状态图标 -->
      <div class="status-icon" :class="statusClass">
        <svg
          v-if="status === 'loading'"
          class="spin-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
        <svg
          v-else-if="status === 'success'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <svg
          v-else-if="status === 'error'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>

      <!-- 状态标题 -->
      <h1 class="status-title">{{ statusTitle }}</h1>
      <p class="status-desc">{{ statusDesc }}</p>

      <!-- 用户信息卡片 -->
      <div v-if="userInfo" class="user-card">
        <div class="user-avatar">
          <img :src="userInfo.avatar" alt="头像" v-if="userInfo.avatar" />
          <div class="avatar-placeholder" v-else>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
        </div>
        <div class="user-info">
          <p class="user-name">
            {{ userInfo.nickname || userInfo.name || "微信用户" }}
          </p>
          <p class="user-tag">登录成功</p>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="action-buttons">
        <button
          v-if="status === 'success'"
          class="primary-btn"
          @click="goToHome"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          去签到
        </button>
        <button
          v-if="status === 'error'"
          class="primary-btn"
          @click="retryLogin"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="1 4 1 10 7 10" />
            <path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
          </svg>
          重新登录
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { weixinCallback } from "@/api/api";

export default {
  name: "WeixinCallback",
  setup() {
    const route = useRoute();
    const router = useRouter();

    const status = ref("loading");
    const statusTitle = ref("正在登录...");
    const statusDesc = ref("微信授权中，请稍候");
    const userInfo = ref(null);
    const pageLoaded = ref(false);

    const statusClass = ref("loading-icon");

    // 处理微信回调
    const handleWeixinCallback = async () => {
      const code = route.query.accesstoken;

      if (!code) {
        status.value = "error";
        statusClass.value = "error-icon";
        statusTitle.value = "登录失败";
        statusDesc.value = "未获取到微信授权码，请重新扫码登录";
        return;
      }

      try {
        const response = await weixinCallback(code);
        console.log(response);

        if (response.data.code === 1) {
          // 登录成功
          const data = response.data.data;
          userInfo.value = data;

          // 保存登录信息到本地存储
          localStorage.setItem("token", response.data.data.accesstoken || "");
          localStorage.setItem("user_id", response.data.data.user_id || "");
          localStorage.setItem("userInfo", JSON.stringify(data));

          status.value = "success";
          statusClass.value = "success-icon";
          statusTitle.value = "登录成功";
          statusDesc.value = "授权登录成功,欢迎使用签到系统";

          // 清除URL中的code和state参数，避免刷新时重复调用
          if (window.history.replaceState) {
            const url = new URL(window.location.href);
            url.search = "";
            window.history.replaceState(null, "", url.toString());
          }

          // 自动跳转到首页
          setTimeout(() => {
            router.push("/");
          }, 2000);
        } else {
          throw new Error(response.data?.message || "登录失败");
        }
      } catch (error) {
        console.error("微信登录失败:", error);
        status.value = "error";
        statusClass.value = "error-icon";
        statusTitle.value = "登录失败";
        statusDesc.value = error.message || "微信授权登录失败，请稍后重试";
      }
    };

    const goToHome = () => {
      router.push("/");
    };

    const retryLogin = () => {
      // 清除可能存在的旧登录信息
      localStorage.removeItem("token");
      localStorage.removeItem("userInfo");
      // 重新调用签到接口获取授权链接
      window.location.reload();
    };

    onMounted(() => {
      setTimeout(() => {
        pageLoaded.value = true;
      }, 100);
      handleWeixinCallback();
    });

    return {
      status,
      statusTitle,
      statusDesc,
      statusClass,
      userInfo,
      pageLoaded,
      goToHome,
      retryLogin,
    };
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;600;700&family=Noto+Sans+SC:wght@300;400;500;700&display=swap");

.callback-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, #e8f4fd 0%, #d6ebfa 50%, #c5e3f8 100%);
  position: relative;
  overflow: hidden;
}

/* Floating bubbles background */
.callback-page::before,
.callback-page::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.4) 0%,
    transparent 70%
  );
  pointer-events: none;
}

.callback-page::before {
  width: 600px;
  height: 600px;
  top: -200px;
  right: -200px;
  animation: float 20s ease-in-out infinite;
}

.callback-page::after {
  width: 400px;
  height: 400px;
  bottom: -100px;
  left: -100px;
  animation: float 25s ease-in-out infinite reverse;
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -30px) scale(1.05);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.95);
  }
}

.callback-card {
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 32px;
  box-shadow: 0 20px 60px rgba(74, 144, 226, 0.15),
    0 4px 20px rgba(74, 144, 226, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  padding: 48px 32px;
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform: translateY(30px);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  text-align: center;
}

.callback-card.animate-in {
  opacity: 1;
  transform: translateY(0);
  transition: all 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

/* Status Icon */
.status-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-icon svg {
  width: 40px;
  height: 40px;
}

.loading-icon {
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  color: white;
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.3);
}

.success-icon {
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  color: white;
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.3);
  animation: scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.error-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%);
  color: white;
  box-shadow: 0 8px 24px rgba(255, 107, 107, 0.3);
  animation: scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.spin-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* Status Text */
.status-title {
  font-family: "Noto Serif SC", serif;
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
  letter-spacing: 2px;
}

.status-desc {
  font-size: 14px;
  color: #8fa1b3;
  margin-bottom: 32px;
  line-height: 1.6;
}

/* User Card */
.user-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(
    135deg,
    rgba(74, 144, 226, 0.06) 0%,
    rgba(90, 200, 250, 0.06) 100%
  );
  border-radius: 16px;
  border: 1px solid rgba(74, 144, 226, 0.1);
  margin-bottom: 24px;
  text-align: left;
}

.user-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.avatar-placeholder svg {
  width: 28px;
  height: 28px;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.user-tag {
  font-size: 12px;
  color: #4a90e2;
  background: rgba(74, 144, 226, 0.1);
  padding: 2px 10px;
  border-radius: 10px;
  display: inline-block;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.primary-btn {
  width: 100%;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  font-family: "Noto Sans SC", sans-serif;
  box-shadow: 0 4px 16px rgba(74, 144, 226, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.4);
}

.primary-btn:active {
  transform: scale(0.98);
}

.primary-btn svg {
  width: 18px;
  height: 18px;
}

/* Responsive */
@media (max-width: 480px) {
  .callback-card {
    padding: 36px 24px;
    border-radius: 24px;
  }

  .status-title {
    font-size: 20px;
  }

  .status-icon {
    width: 64px;
    height: 64px;
  }

  .status-icon svg {
    width: 32px;
    height: 32px;
  }

  .user-card {
    padding: 16px;
  }
}
</style>
